"use client";

import { m } from "motion/react";
import { usePathname, useRouter } from "next/navigation";
import { useLocale } from "@/lib/i18n";
import {
  locales,
  defaultLocale,
  localeHref,
  type Locale,
} from "@/lib/i18n/config";

const order: Locale[] = ["en", "sq", "ar"];

/**
 * Brand-themed language switcher. A 3-segment pill (EN / SQ / AR) with a
 * sliding indicator (motion layoutId). Locale lives in the URL now, so a
 * switch is navigation: strip the current locale prefix from the path and
 * re-prefix with the target (the default locale stays unprefixed).
 */
export function LocaleSwitcher({ className = "" }: { className?: string }) {
  const { locale, t } = useLocale();
  const pathname = usePathname();
  const router = useRouter();

  const switchTo = (next: Locale) => {
    // strip any existing non-default locale prefix to get the bare path
    let bare = pathname || "/";
    for (const l of locales) {
      if (l === defaultLocale) continue;
      if (bare === `/${l}`) {
        bare = "/";
        break;
      }
      if (bare.startsWith(`/${l}/`)) {
        bare = bare.slice(`/${l}`.length);
        break;
      }
    }
    router.push(localeHref(next, bare));
  };

  return (
    <div
      className={`inline-flex items-center gap-0.5 rounded-full border border-[var(--color-hairline)] bg-[color:var(--color-navy-raised)] p-0.5 ${className}`}
      role="group"
      aria-label={t.locale.switcherAria}
      style={{
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
      }}
    >
      {order.map((l) => {
        const active = l === locale;
        return (
          <button
            key={l}
            type="button"
            onClick={() => switchTo(l)}
            aria-pressed={active}
            aria-label={`${t.locale.label}: ${t.locale[l]}`}
            className="relative px-2.5 py-1 text-[11px] font-medium tracking-[0.08em] uppercase rounded-full transition-colors focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--color-blue)] focus-visible:outline-offset-1"
            style={{ fontFamily: "var(--font-mono)" }}
          >
            {active ? (
              <m.span
                layoutId="locale-active-pill"
                className="absolute inset-0 rounded-full bg-[var(--color-blue)]"
                transition={{
                  type: "spring",
                  stiffness: 380,
                  damping: 32,
                }}
              />
            ) : null}
            <span
              className={`relative z-10 ${
                active
                  ? "text-[var(--color-navy)]"
                  : "text-[var(--color-text-mute)] hover:text-[var(--color-text)]"
              }`}
            >
              {t.locale[l]}
            </span>
          </button>
        );
      })}
    </div>
  );
}
